import { useState, useEffect, useCallback, useRef } from "react";
import { showAlert, showConfirm } from '@/components/AppDialog';
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import MobileNav from "../components/MobileNav";
import UserProfileMenu from "../components/UserProfileMenu";
import { storage, auth } from "../firebase";
import { signOut } from "firebase/auth";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject, listAll, getMetadata } from "firebase/storage";
import { useAuth } from "../hooks/useAuth";
import { API_BASE_URL } from "@/utils/env";
import { Presentation, Wallet, Scale, Puzzle, ReceiptText, FileText, type LucideIcon } from "lucide-react";

type UploadedFile = {
  id?: string;
  name: string;
  url: string;
  uploadedAt: string;
  size: number;
  storagePath: string;
  uploadedBy?: string | null;
  uploadedByAdmin?: boolean;
};

type Cat = { id: string; icon: LucideIcon; title: string; desc: string };
type GroupKey = "consulting" | "accounting";

const CONSULTING_CATS: Cat[] = [
  { id: "Pitch Materials",   icon: Presentation, title: "Pitch Materials",    desc: "Deck, one-pager, teaser" },
  { id: "Financials",        icon: Wallet,       title: "Financials",          desc: "P&L, cash flow, forecast" },
  { id: "Legal",             icon: Scale,        title: "Legal",              desc: "Incorporation, cap table, agreements" },
  { id: "Product",           icon: Puzzle,       title: "Product",             desc: "Demo, docs, screenshots" },
];

const ACCOUNTING_CATS: Cat[] = [
  { id: "acc-tax",     icon: ReceiptText, title: "Tax Documents",       desc: "VAT & Corporate Tax certificates, returns & filings" },
  { id: "acc-licence", icon: FileText,    title: "Commercial Licence",   desc: "Trade / commercial licence document" },
];

const GROUPS: Record<GroupKey, {
  label: string;
  cats: Cat[];
  accent: string;
  accentBg: string;
  theme: "consulting" | "accounting";
  desc: string;
  activatePath: string;
}> = {
  consulting: {
    label: "Consulting", cats: CONSULTING_CATS, accent: "#1976d2", accentBg: "#EFF6FF",
    theme: "consulting", desc: "Pitch, financials, legal & product documents", activatePath: "/consulting",
  },
  accounting: {
    label: "Accounting", cats: ACCOUNTING_CATS, accent: "#D97706", accentBg: "#FFFBEB",
    theme: "accounting", desc: "Tax & compliance documents", activatePath: "/accounting",
  },
};

const ALL_CATS = [...CONSULTING_CATS, ...ACCOUNTING_CATS];

function sanitize(name: string) {
  const t = name.trim();
  if (!t) return `file_${Date.now()}`;
  return t.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_.-]/g, "_");
}

function fmtSize(bytes: number) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

// Upload icon
const UpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);
// Download icon
const DlIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
// Trash icon
const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);
// Chevron icon
const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transition: "transform .2s", transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

export default function DataRoom({ primary = "consulting" }: { primary?: GroupKey }) {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshAuth } = useAuth();

  // Always return a fresh (non-expired) idToken for backend API calls.
  // Stored in a ref so loadFiles (useCallback with [] deps) always sees the latest version
  // without needing to be recreated on every render.
  const getFreshTokenRef = useRef<() => Promise<string | null>>(async () => null);
  getFreshTokenRef.current = async () => {
    await refreshAuth().catch(() => {});
    const tokensStr = localStorage.getItem('authTokens');
    return tokensStr ? JSON.parse(tokensStr).idToken : null;
  };

  // Track whether the first load has completed so re-runs don't flash the full-page spinner.
  const initialLoadDone = useRef(false);

  // The page's own service is the "primary" group (always unlocked); the other
  // group is "secondary" and stays locked until its module is activated.
  //
  // The CONSULTING data room is consulting-only — Accounting is deliberately not
  // listed here at all, even for a client who owns both. Their tax documents live
  // in /accounting/data-room, which is where the sidebar points as soon as the
  // accounting view is active (see Sidebar.tsx), so nothing becomes unreachable.
  const order: GroupKey[] = primary === "accounting" ? ["accounting", "consulting"] : ["consulting"];

  const [selectedId, setSelectedId]     = useState<string>("all");
  const [openGroups, setOpenGroups]     = useState<Record<GroupKey, boolean>>({ consulting: true, accounting: true });
  const [search, setSearch]             = useState("");
  const [categoryFiles, setCategoryFiles] = useState<Record<string, UploadedFile[]>>({});
  const [loading, setLoading]           = useState(true);
  const [uploadingCat, setUploadingCat] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [hasAccountingService, setHasAccountingService] = useState(false);
  const [hasConsultingService, setHasConsultingService] = useState(false);

  // A group is unlocked if it's the page's primary service, the user has that module
  // active, OR the user already has documents stored in that group's categories.
  const groupHasFiles = (key: GroupKey) =>
    GROUPS[key].cats.some(c => (categoryFiles[c.id]?.length || 0) > 0);

  const isUnlocked = (key: GroupKey) =>
    key === primary ||
    (key === "accounting" ? hasAccountingService : hasConsultingService) ||
    groupHasFiles(key);

  // Load files DIRECTLY from Firebase Storage — the single source of truth.
  // Storage rules allow read/write on dataRoom/** without auth, so this works
  // regardless of Firebase client-auth state or backend/database availability.
  const loadFiles = useCallback(async (uid: string) => {
    try {
      if (!initialLoadDone.current) setLoading(true);

      const result: Record<string, UploadedFile[]> = {};
      ALL_CATS.forEach(cat => { result[cat.id] = []; });

      await Promise.all(ALL_CATS.map(async (cat) => {
        try {
          const folderRef = storageRef(storage, `dataRoom/${uid}/${cat.id}`);
          const listing = await listAll(folderRef);
          const files = await Promise.all(listing.items.map(async (itemRef) => {
            const [url, meta] = await Promise.all([
              getDownloadURL(itemRef).catch(() => ""),
              getMetadata(itemRef).catch(() => null as any),
            ]);
            // Prefer the original filename saved in customMetadata; otherwise strip the
            // leading "<timestamp>_" prefix from the stored object name.
            const friendly = meta?.customMetadata?.originalName || itemRef.name.replace(/^\d+_/, "");
            return {
              id: itemRef.fullPath,
              name: friendly,
              url,
              uploadedAt: meta?.timeCreated || new Date().toISOString(),
              size: meta?.size || 0,
              storagePath: itemRef.fullPath,
              uploadedBy: meta?.customMetadata?.uploadedBy || null,
              uploadedByAdmin: meta?.customMetadata?.uploadedByAdmin === 'true',
            } as UploadedFile;
          }));
          result[cat.id] = files
            .filter(f => f.url)
            .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
        } catch {
          // Folder doesn't exist yet (no uploads in this category) — leave empty.
        }
      }));

      setCategoryFiles(result);
      initialLoadDone.current = true;
    } catch (e) { console.error('loadFiles error:', e); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine which secondary group is unlocked, based ONLY on a genuine purchase of
  // that specific service (so registering for one module never auto-activates the other).
  // Accounting packages carry a `flags` array; consulting packages have a name but no flags.
  // A sticky flag is used only as a fallback if the backend read fails.
  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();
    const tokensStr = localStorage.getItem("authTokens");
    const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
    fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
      signal: controller.signal,
      headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
    })
      .then(r => r.json())
      .then(json => {
        const u = json.user || {};
        const pkgsRaw = u.purchasedPackages;
        const allPkgs: any[] = pkgsRaw
          ? (Array.isArray(pkgsRaw) ? pkgsRaw : Object.values(pkgsRaw) as any[])
              .filter((p: any) => p?.name)
          : [];
        if (!allPkgs.length && u.purchasedPackage?.name) allPkgs.push(u.purchasedPackage);

        // Accounting is activated by EITHER a purchased accounting package (a package
        // carrying a `flags` array: bookkeeping/VAT/CT) OR by completing the accounting
        // setup (the operate/incorporate flow writes `businessSetupData`). This mirrors
        // how consulting is detected (explicit flag), so an accounting user is never
        // wrongly shown "Activate Accounting". All signals come from the per-account
        // backend record `u`, so there is no stale-localStorage bleed.
        const hasAcc =
          allPkgs.some((p: any) => Array.isArray(p.flags))
          || u.accountingActivated === true
          || u.businessSetupData?.type === 'operate'
          || u.businessSetupData?.type === 'incorporate'
          || !!u.businessSetupData?.submittedAt;

        // Consulting is activated EXPLICITLY — by pressing "Activate Consulting" on the dashboard
        // or completing the consulting onboarding (both set `consultingActivated`), or by holding a
        // consulting package. We intentionally do NOT infer it from the goal assessment / `/goal`,
        // because accounting users complete that assessment too.
        const hasCon =
          u.consultingActivated === true
          || allPkgs.some((p: any) => p.name && !Array.isArray(p.flags));

        // Authoritative: set strictly from real packages and heal sticky flags to match,
        // so a stale localStorage value can't keep a module wrongly unlocked.
        setHasAccountingService(hasAcc);
        setHasConsultingService(hasCon);
        localStorage.setItem('dr_hasAccounting', hasAcc ? 'true' : 'false');
        localStorage.setItem('dr_hasConsulting', hasCon ? 'true' : 'false');
      })
      .catch(e => {
        if (e?.name === "AbortError") return;
        console.error(e);
        // Only when the read fails do we fall back to the last known sticky state,
        // so a flaky backend can't re-lock an already-activated module.
        if (localStorage.getItem('dr_hasAccounting') === 'true') setHasAccountingService(true);
        if (localStorage.getItem('dr_hasConsulting') === 'true') setHasConsultingService(true);
      });
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    if (!authLoading && user) loadFiles(user.uid);
    else if (!authLoading && !user) setLoading(false);
  // user?.uid: only re-run when the actual UID changes, not on user object reference recreation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, authLoading, loadFiles]);

  const handleUpload = (catId: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.style.display = "none";
    document.body.appendChild(input);
    input.onchange = async (e: any) => {
      document.body.removeChild(input);
      if (e.target.files?.length) await uploadFiles(e.target.files, catId);
    };
    input.click();
  };

  const uploadFiles = async (files: FileList, catId: string) => {
    if (!user) { showAlert("Please sign in to upload files."); return; }
    setUploadingCat(catId);
    setUploadProgress(0);
    const uploaded: UploadedFile[] = [];
    const idToken = await getFreshTokenRef.current();
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file) continue;
        const ts = Date.now();
        const safeName = sanitize(file.name);
        // Always key uploads by the app's canonical uid — the same id the client's own
        // listing (loadFiles), the DB mirror, and the ADMIN read all use. Using
        // auth.currentUser?.uid here caused files to land under a stale/other Firebase
        // session's uid, so they never showed up for the admin.
        const uploaderUid = user.uid;
        const path = `dataRoom/${uploaderUid}/${catId}/${ts}_${safeName}`;
        const fRef = storageRef(storage, path);

        // Persist the original filename + uploader in Storage object metadata so
        // listing can restore them (Storage is the source of truth on reload).
        const uploadMeta = {
          customMetadata: {
            originalName: file.name,
            uploadedBy: user.email ?? "",
          },
        };
        const task = uploadBytesResumable(fRef, file, uploadMeta);
        const downloadURL: string = await new Promise((resolve, reject) => {
          task.on("state_changed",
            snap => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
            reject,
            async () => resolve(await getDownloadURL(task.snapshot.ref))
          );
        });

        const record: UploadedFile = {
          id: path,
          name: file.name,
          url: downloadURL,
          uploadedAt: new Date().toISOString(),
          size: file.size,
          storagePath: path,
          uploadedBy: user.email ?? null,
        };

        // Best-effort: also record metadata in the backend DB so admin views can see it.
        // The file already lives in Storage (the source of truth), so failure here is non-fatal.
        fetch(`${API_BASE_URL}/auth/user/${user.uid}/dataroom/${catId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
          body: JSON.stringify({ uid: user.uid, catId, ...record }),
        }).catch(() => {});

        uploaded.push(record);
      }
      setCategoryFiles(prev => ({ ...prev, [catId]: [...uploaded, ...(prev[catId] || [])] }));
    } catch (e: any) {
      showAlert("Upload failed: " + (e.message || "Unknown error"));
    } finally {
      setUploadProgress(null);
      setUploadingCat(null);
    }
  };

  const handleDelete = async (catId: string, file: UploadedFile) => {
    if (!user) return;
    if (!(await showConfirm(`Delete "${file.name}"?`, { destructive: true, confirmText: 'Delete' }))) return;
    try {
      // Storage is the source of truth — delete the object there.
      await deleteObject(storageRef(storage, file.storagePath));

      // Best-effort: also remove any backend DB record (non-fatal if it fails).
      const idToken = await getFreshTokenRef.current();
      fetch(`${API_BASE_URL}/auth/user/${user.uid}/dataroom/${encodeURIComponent(file.storagePath)}`, {
        method: 'DELETE',
        headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
      }).catch(() => {});

      setCategoryFiles(prev => ({ ...prev, [catId]: (prev[catId] || []).filter(f => f.storagePath !== file.storagePath) }));
    } catch (e) { showAlert("Failed to delete file."); }
  };

  const handleSignOut = () => {
    signOut(auth).catch(() => {}).finally(() => {
      localStorage.removeItem("user");
      localStorage.removeItem("dr_hasAccounting");
      localStorage.removeItem("dr_hasConsulting");
      navigate("/");
    });
  };

  // Search filter
  const fileCount = (catId: string) => {
    const files = categoryFiles[catId] || [];
    if (!search) return files;
    return files.filter(f => f.name.toLowerCase().includes(search.toLowerCase()));
  };

  const catsToShow = (g: { cats: Cat[] }) => {
    if (selectedId === "all") return g.cats;
    return g.cats.filter(c => c.id === selectedId);
  };

  // Only surface data-room groups the user actually owns. The other service is never
  // offered for "unlock/activate" from inside the data room.
  const visibleGroups = order.filter(k => isUnlocked(k));

  // Counts and labels follow what's on screen — counting ALL_CATS here would make the
  // consulting page claim documents it doesn't list (the user's accounting uploads).
  const visibleCats = visibleGroups.flatMap(k => GROUPS[k].cats);
  const totalFiles = visibleCats.reduce((acc, c) => acc + (categoryFiles[c.id]?.length || 0), 0);

  const selectedLabel =
    selectedId === "all" ? "All Documents"
      : (GROUPS as any)[selectedId]?.label
      || visibleCats.find(c => c.id === selectedId)?.title
      || "All Documents";

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#EBEBEF]">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EBEBEF] text-slate-900">
      <Sidebar onLogout={handleSignOut} />
      <main className="md:ms-64 flex-1 overflow-y-auto bg-[#EBEBEF] relative min-h-screen">
        <MobileNav onLogout={handleSignOut} />
        <div className="absolute top-6 end-8 z-10"><UserProfileMenu /></div>

        <div className="mx-auto max-w-[1200px] px-6 py-12 mt-8">
          {/* Breadcrumb */}
          <div className="mb-2 flex items-center gap-1 text-sm text-[#6B7280]">
            <button onClick={() => navigate("/dashboard")} className="hover:text-gray-900">My Applications</button>
            <span>/</span>
            <span className="text-[#111827] font-medium">Data Room</span>
          </div>
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-[36px] font-bold text-[#111827] leading-tight">Data Room</h1>
              <p className="text-sm text-[#6B7280] mt-1">{totalFiles} document{totalFiles !== 1 ? "s" : ""} stored securely</p>
            </div>
          </div>

          {/* Two-column layout */}
          <div className="flex gap-5">
            {/* ── Left Tree Nav ── */}
            <div className="hidden md:flex flex-col w-56 shrink-0">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* All Documents */}
                <button
                  onClick={() => setSelectedId("all")}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm font-semibold border-b border-gray-50 transition-colors ${selectedId === "all" ? "bg-blue-50 text-[#1976d2]" : "text-[#374151] hover:bg-gray-50"}`}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  All Documents
                </button>

                {/* Groups */}
                {visibleGroups.map((key, gi) => {
                  const g = GROUPS[key];
                  const unlocked = true; // only owned groups are rendered
                  const open = openGroups[key];
                  const acct = g.theme === "accounting";
                  const activeHeader = selectedId === key;
                  return (
                    <div key={key} className={gi < visibleGroups.length - 1 ? "border-b border-gray-50" : ""}>
                      <button
                        onClick={() => { setOpenGroups(o => ({ ...o, [key]: !o[key] })); if (unlocked) setSelectedId(key); }}
                        className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                          activeHeader ? (acct ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-[#1976d2]") : "text-[#6B7280] hover:bg-gray-50"
                        }`}
                      >
                        <ChevronIcon open={open} />
                        <span className="flex-1 text-left">{g.label}</span>
                        {unlocked ? (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${acct ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-[#1976d2]"}`}>
                            {g.cats.reduce((acc, c) => acc + (categoryFiles[c.id]?.length || 0), 0)}
                          </span>
                        ) : (
                          <span className="text-base">🔒</span>
                        )}
                      </button>
                      {open && (
                        <div className="pb-1">
                          {g.cats.map(cat => (
                            <button
                              key={cat.id}
                              disabled={!unlocked}
                              onClick={() => unlocked && setSelectedId(cat.id)}
                              className={`w-full flex items-center gap-2 pl-8 pr-4 py-2 text-sm transition-colors ${
                                !unlocked ? "text-gray-300 cursor-not-allowed" :
                                selectedId === cat.id ? (acct ? "bg-amber-50 text-amber-700 font-semibold" : "bg-blue-50 text-[#1976d2] font-semibold") : "text-[#374151] hover:bg-gray-50"
                              }`}
                            >
                              <cat.icon className="w-4 h-4 shrink-0" />
                              <span className="flex-1 text-left truncate">{cat.title}</span>
                              {unlocked && (categoryFiles[cat.id]?.length || 0) > 0 && (
                                <span className="text-[10px] text-gray-400 font-medium">{categoryFiles[cat.id]?.length}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Right content ── */}
            <div className="flex-1 min-w-0">
              {/* Search + header row */}
              <div className="flex items-center gap-3 mb-5">
                <div className="relative flex-1 max-w-sm">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search documents…"
                    className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                  />
                </div>
                <span className="text-sm font-semibold text-[#374151]">{selectedLabel}</span>
              </div>

              {/* Group sections (in page order) */}
              {visibleGroups.map(key => {
                const g = GROUPS[key];
                const show = selectedId === "all" || selectedId === key || g.cats.some(c => c.id === selectedId);
                if (!show) return null;
                const acct = g.theme === "accounting";

                return (
                  <div key={key} className="mb-6">
                    {(selectedId === "all" || selectedId === key) && (
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-0.5 text-xs font-semibold border rounded-full ${acct ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-[#1976d2] border-blue-200"}`}>{g.label}</span>
                        <span className="text-xs text-[#6B7280]">{g.desc}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {catsToShow(g).map(cat => (
                        <CategoryCard
                          key={cat.id}
                          cat={cat}
                          files={fileCount(cat.id)}
                          isUploading={uploadingCat === cat.id}
                          uploadProgress={uploadProgress}
                          onUpload={() => handleUpload(cat.id)}
                          onDropFiles={(files) => uploadFiles(files, cat.id)}
                          onDownload={f => window.open(f.url, "_blank")}
                          onDelete={f => handleDelete(cat.id, f)}
                          accentColor={g.accent}
                          accentBg={g.accentBg}
                          theme={g.theme}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// ── Category card component ────────────────────────────────────────────────────
const isImage = (name: string) => /\.(png|jpe?g|gif|webp|svg|avif|bmp)$/i.test(name || "");

function CategoryCard({
  cat, files, isUploading, uploadProgress, onUpload, onDropFiles, onDownload, onDelete, accentColor, accentBg, theme,
}: {
  cat: Cat;
  files: UploadedFile[];
  isUploading: boolean;
  uploadProgress: number | null;
  onUpload: () => void;
  onDropFiles: (files: FileList) => void;
  onDownload: (f: UploadedFile) => void;
  onDelete: (f: UploadedFile) => void;
  accentColor: string;
  accentBg: string;
  theme: "consulting" | "accounting";
}) {
  const [drag, setDrag] = useState(false);
  const btnCls = theme === "consulting"
    ? "bg-[#1976d2] text-white hover:bg-blue-700"
    : "bg-amber-500 text-white hover:bg-amber-600";

  return (
    <div
      className="rounded-xl border bg-white p-5 shadow-sm hover:shadow-md transition-all"
      style={{ borderColor: drag ? accentColor : "#E5E7EB", background: drag ? accentBg : "#fff" }}
      onDragOver={(e) => { e.preventDefault(); if (!drag) setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) onDropFiles(e.dataTransfer.files); }}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: accentBg }}>
          <cat.icon className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-[#111827]">{cat.title}</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">{cat.desc}</p>
        </div>
      </div>

      <div className="mb-4 min-h-[80px]">
        {drag ? (
          <div className="flex flex-col items-center justify-center h-[80px] border-2 border-dashed rounded-lg text-center" style={{ borderColor: accentColor, background: accentBg }}>
            <p className="text-xs font-bold" style={{ color: accentColor }}>Drop to upload</p>
          </div>
        ) : files.length > 0 ? (
          <div className="space-y-2 max-h-44 overflow-y-auto">
            {files.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 group hover:border-gray-200">
                <div className="flex items-center gap-2.5 flex-1 min-w-0 mr-2">
                  {isImage(file.name) && file.url ? (
                    <img src={file.url} alt="" className="w-8 h-8 rounded object-cover border border-gray-200 flex-none" loading="lazy" />
                  ) : (
                    <span className="w-8 h-8 rounded flex-none grid place-items-center text-[9px] font-black text-gray-400 bg-gray-100 border border-gray-200">
                      {(file.name.split('.').pop() || 'FILE').slice(0, 4).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#374151] truncate">{file.name}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {file.uploadedByAdmin && <span className="text-emerald-600 font-bold">Delivered by your advisor · </span>}
                      {fmtSize(file.size)} · {new Date(file.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => onDownload(file)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Download / open" style={{ color: accentColor }}><DlIcon /></button>
                  <button onClick={() => onDelete(file)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><TrashIcon /></button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[80px] border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/60 text-center">
            <p className="text-xs text-gray-400 font-medium">No files yet</p>
            <p className="text-[11px] text-gray-300 mt-0.5">Drag &amp; drop or upload {cat.title.toLowerCase()}</p>
          </div>
        )}
      </div>

      <button
        onClick={onUpload}
        disabled={isUploading}
        className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 disabled:opacity-50 ${btnCls}`}
      >
        {isUploading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Uploading {uploadProgress !== null ? `${uploadProgress}%` : "…"}</span>
          </>
        ) : (
          <><UpIcon /><span>Upload file</span></>
        )}
      </button>
    </div>
  );
}
